import { environment } from '@environments/environment';
import { Component, Inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '@app/services/auth/auth.service';
import { Hub } from 'aws-amplify/utils';
import { analytics } from '@app/events/segment/analytics';
import { getEmailBaseDomain } from '@app/utils/email-utils';
import { UserService, UserProfile } from '@app/services/api/user/user.service';
import { LoadingSpinnerComponent } from '@app/shared/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-login-redirect',
  imports: [LoadingSpinnerComponent],
  template: `
    <app-loading-spinner></app-loading-spinner>
  `,
  standalone: true
})
export class LoginRedirectComponent implements OnDestroy {
  private hubListenerCancel: (() => void) | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
  ) {}

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Check if this is an OAuth callback (has code or token in URL)
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthCode = urlParams.has('code') || urlParams.has('state');

      if (hasOAuthCode) {
        // This is an OAuth callback - listen for Hub events
        console.log('OAuth callback detected, waiting for auth events...');
        await this.waitForOAuthCompletion();
      } else {
        // Not an OAuth callback - check if user is already authenticated
        // This handles both Cognito sessions and oidc_jwt cookie
        console.log('No OAuth callback, checking existing auth status...');
        await this.authService.init();

        if (this.authService.isAuthenticated()) {
          console.log('User already authenticated (Cognito or idaas oidc), redirecting...');
          this.identifyUserAndRedirect();
        } else {
          // Not authenticated, redirect to oidc idaas login
          console.log('User not authenticated, redirecting to login...');
          window.location.href = environment.loginUrl;
        }
      }
    }
  }

  ngOnDestroy() {
    // Clean up Hub listener
    if (this.hubListenerCancel) {
      this.hubListenerCancel();
    }
  }

  private waitForOAuthCompletion(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Set a timeout to prevent hanging forever
      const timeout = setTimeout(() => {
        console.error('OAuth callback timed out');
        this.cleanupAndRedirectToLogin();
        reject(new Error('OAuth timeout'));
      }, 10000); // 10 second timeout

      // Listen for Amplify Hub auth events
      this.hubListenerCancel = Hub.listen('auth', async ({ payload }) => {
        console.log('Hub auth event:', payload.event);

        if (payload.event === 'signedIn') {
          clearTimeout(timeout);
          console.log('Sign in successful via Hub event');

          // Re-initialize auth state
          await this.authService.init();

          if (this.authService.isAuthenticated()) {
            console.log('User authenticated via SSO, redirecting...');
            this.identifyUserAndRedirect();
          } else {
            console.error('Hub signedIn event but user not authenticated');
            this.cleanupAndRedirectToLogin();
          }
          resolve();
        } else if (payload.event === 'signedOut') {
          // User signed out unexpectedly during OAuth flow
          clearTimeout(timeout);
          console.error('User signed out during OAuth flow');
          this.cleanupAndRedirectToLogin();
          reject(new Error('User signed out'));
        }
      });
    });
  }

  private identifyUserAndRedirect(): void {
    const currentUser = this.authService.getCurrentUser();
    this.userService.getMe().subscribe({
      next: (userProfile: UserProfile) => {
        if (userProfile.userId) {
          analytics.identify(userProfile.userId, {
            sso_campus_id: currentUser?.campusId,
            email_base_domain: getEmailBaseDomain(userProfile.email),
          });
        }
        localStorage.removeItem('redirectUrl');
        this.router.navigateByUrl('/career-matches');
      },
      error: (err) => {
        console.error('Error fetching user profile for analytics:', err);
        localStorage.removeItem('redirectUrl');
        this.router.navigateByUrl('/career-matches');
      }
    });
  }

  private cleanupAndRedirectToLogin() {
    if (this.hubListenerCancel) {
      this.hubListenerCancel();
      this.hubListenerCancel = null;
    }
    window.location.href = environment.loginUrl;
  }
}


